import { parseOVA, parseRA, } from '@/store/modules/record/helpers/parsing'

import { timeFormat, } from '@/store/helpers'

export default [
  {
    label: 'Registration Date',
    type: 'DATE',
    inline: true,
    text: 'Date/time the LEI record was created.',
    fieldId: 'LEIREC_INITIAL_REGISTRATION_DATE',
    jsonPath: '$.registration.initialRegistrationDate',
  },
  {
    label: 'Last Update',
    type: 'DATE',
    inline: true,
    text: 'Date/time the LEI record was most recently updated.',
    fieldId: 'LEIREC_LAST_UPDATE_DATE',
    jsonPath: '$.registration.lastUpdateDate',
  },
  {
    label: 'Status',
    isStatus: true,
    type: 'ENUM',
    inline: true,
    hardCodeEnum: true,
    enum: [
      'ISSUED',
      'LAPSED',
      'ANNULLED',
      'PENDING_TRANSFER',
      'PENDING_ARCHIVAL',
      'DUPLICATE',
      'RETIRED',
    ],
    fieldId: 'LEIREC_REGISTRATION_STATUS',
    text: 'The status of the legal entity\'s LEI registration with the ManagingLOU.',
    jsonPath: '$.registration.status',
  },
  {
    label: 'Next Renewal',
    type: 'DATE',
    inline: true,
    text: 'The next date by which the LEI registration must be renewed and re-certified by the legal entity.',
    fieldId: 'LEIREC_NEXT_RENEWAL_DATE',
    jsonPath: '$.registration.nextRenewalDate',
  },
  {
    label: 'LEI Issuer',
    isLeiIssuer: true,
    type: 'RESOURCE',
    inline: true,
    fieldId: 'LEIREC_MANAGING_LOU',
    text: 'The LEI of the LEI Issuer (LOU) that is responsible for administering this LEI registration.',
    jsonPath: '$.registration.managingLou',
  },
  {
    label: 'Corroboration Level',
    type: 'ENUM',
    inline: true,
    fieldId: 'LEIREC_VALIDATION_SOURCES',
    text: 'The level of validation of the reference data provided by the registrant.',
    jsonPath: '$.registration.corroborationLevel',
  },
  {
    label: 'Data Validated At',
    type: 'RESOURCE',
    getValue: parseRA,
    inline: true,
    fieldId: 'LEIREC_REGISTERED_AT',
    exchange: 'LEIREC_OTHER_VALIDATION_AUTHORITY_ID',
    alternativeInput: true,
    text: 'A code (from the official GLEIF Registration Authority List) for the primary validation authority.',
    jsonPath: '$.registration.validatedAt',
  },
  {
    label: 'Data Validated As',
    type: 'STRING',
    inline: true,
    fieldId: 'LEIREC_VALIDATION_AUTHORITY_ENTITY_ID',
    text: 'The identifier of the entity at the indicated registration authority',
    jsonPath: '$.registration.validatedAs',
  },
  {
    label: 'Other Validation Authorities',
    jsonPath: '$.registration.otherValidationAuthorities',
    multiple: true,
  },
  {
    label: 'Data Validated At',
    fieldId: 'LEIREC_OTHER_VALIDATION_AUTHORITIES_AUTHORITY_ID',
    type: 'STRING',
    getValue: parseOVA,
    withLabel: true,
    inline: true,
    jsonPath: '$.registration.otherValidationAuthorities[*].validatedAt.id',
  },
  {
    label: 'Data Validated At (OTHER ID)',
    fieldId: 'LEIREC_OTHER_VALIDATION_AUTHORITIES_OTHER_AUTHORITY_ID',
    type: 'STRING',
    withLabel: true,
    inline: true,
    jsonPath: '$.registration.otherValidationAuthorities[*].validatedAt.other',
  },
  {
    label: 'Data Validated As',
    fieldId: 'LEIREC_OTHER_VALIDATION_AUTHORITIES_ENTITY_ID',
    type: 'STRING',
    withLabel: true,
    inline: true,
    jsonPath: '$.registration.otherValidationAuthorities[*].validatedAs',
  },
]

const parseOtherValidation = (data) => {
  if (!data && !Array.isArray(data)) return

  if (!data.length) return [
    {
      value: '',
      jsonPath: '$.registration.otherValidationAuthorities[*].validatedAt.id',
    },
    {
      value: '',
      prerequisite: true,
      jsonPath: '$.registration.otherValidationAuthorities[*].validatedAs',
    },
  ]

  return data
    .filter(({ validatedAt, validatedAs, }) =>
      validatedAt && validatedAs
    )
    .reduce((acc, { validatedAt, validatedAs, }, i) => {
      Object.keys(validatedAt).forEach((key) => {
        acc.push({
          value: validatedAt[key],
          jsonPath: `$.registration.otherValidationAuthorities[${i}].validatedAt.${key}`,
        })
      })

      acc.push({
        value: validatedAs,
        jsonPath: `$.registration.otherValidationAuthorities[${i}].validatedAs`,

      })

      return acc
    }, [])
}

export const parseRegistrationDetails =
  ({ registration, leiIssuer = null, parentType = null, prefix = null, }) => {
    const processedPrefix = prefix ? `${prefix}.` : (parentType ? `${parentType}.` : '')
    const createField = (prop, value) => ({
      value: value || registration[prop],
      jsonPath: `$.${processedPrefix}registration.${prop}`,
    })
    const validatedAtID = registration.validatedAt ? registration.validatedAt.id : null
    const validatedFields = [ createField('validatedAt', validatedAtID), createField('validatedAs'), ]
    const corroboratingFields = [ createField('corroborationDocuments'), createField('corroborationReference'), ]
    const additionalFields = !parentType ? validatedFields : corroboratingFields
    const otherValidationAuthorities = registration.otherValidationAuthorities ? [ createField('otherValidationAuthorities', parseOtherValidation(registration.otherValidationAuthorities)), ] : []
    return [
      createField('initialRegistrationDate', timeFormat(registration.initialRegistrationDate)),
      createField('lastUpdateDate', timeFormat(registration.lastUpdateDate)),
      createField('status'),
      createField('nextRenewalDate', timeFormat(registration.nextRenewalDate)),
      createField('managingLou', leiIssuer || registration.managingLou),
      createField('corroborationLevel'),
      ...additionalFields,
      ...otherValidationAuthorities,
    ]
  }

export const getEntityBusinessRegistryLinkMapper = (raCode) => {
  const ukCodes = [ 'RA000585', 'RA000586', 'RA000587', ]

  const mappers = {
    'RA000463': {
      getUrl (registrationNumber) {
        return `https://kvk.nl/bestellen/?kvknummer=${registrationNumber}`
      },
      getTooltipMessage () {
        return 'Click here to go to the official business registry data (in local language)'
      },
    },
    UK: {
      getUrl (registrationNumber) {
        return `https://find-and-update.company-information.service.gov.uk/company/${registrationNumber}`
      },
      getTooltipMessage () {
        return 'Click here to go to the official business registry data (in local language)'
      },
    },
  }

  if (ukCodes.includes(raCode)) {
    return mappers.UK
  }

  return mappers[raCode] || null
}


// WEBPACK FOOTER //
// ./src/utils/challenge/registration-details.js